import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, Archive, ChevronDown, ChevronUp, ChevronsUpDown, Users } from 'lucide-react'

function formatCurrency(v) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(v ?? 0)
}

const COL_HEADERS = [
  { key: 'name',                  label: 'Client',         align: 'left'  },
  { key: 'mtd_total',             label: 'Total',          align: 'right' },
  { key: 'mtd_order_count',       label: 'Orders',         align: 'right' },
  { key: 'mtd_orders_fulfilment', label: 'Fulfilment',     align: 'right' },
  { key: 'mtd_orders_shipping',   label: 'Shipping',       align: 'right' },
  { key: 'mtd_sku_charges',       label: 'Order Details',  align: 'right' },
  { key: 'mtd_returns',           label: 'Returns',        align: 'right' },
  { key: 'mtd_exports',           label: 'Exports',        align: 'right' },
  { key: 'mtd_storage',           label: 'Storage',        align: 'right' },
  { key: 'mtd_activity',          label: 'Activity',       align: 'right' },
]

function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <ChevronsUpDown size={11} className="text-slate-300 ml-1 inline-block" />
  return sortDir === 'asc'
    ? <ChevronUp size={11} className="text-blue-500 ml-1 inline-block" />
    : <ChevronDown size={11} className="text-blue-500 ml-1 inline-block" />
}

/**
 * All-clients summary table, used on both the live admin overview and the
 * historical billing periods page. The data shape is identical in both cases
 * (per `AdminClientSummary`); only the period the data was computed for differs.
 *
 * @param {object}   props
 * @param {Array}    props.clients              — list of AdminClientSummary
 * @param {boolean}  [props.isLoading]
 * @param {object}   [props.totals]             — { mtd_total, mtd_orders_fulfilment, ... }
 * @param {object}   [props.period]             — { year, month } passed to client detail link as ?from=YYYY-MM-01&to=YYYY-MM-DD
 */
export default function AdminClientsTable({ clients, isLoading, totals, totalsActive, period }) {
  const navigate = useNavigate()
  const [sortCol, setSortCol] = useState('name')
  const [sortDir, setSortDir] = useState('asc')

  const totalOrders = clients.reduce((s, c) => s + (c.mtd_order_count ?? 0), 0)
  const totalOrdersActive = clients.filter(c => !c.archived).reduce((s, c) => s + (c.mtd_order_count ?? 0), 0)
  const archivedCount = clients.filter(c => c.archived).length

  function handleSort(key) {
    if (sortCol === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortCol(key)
      setSortDir(key === 'name' ? 'asc' : 'desc')
    }
  }

  function clientHref(id) {
    if (!period) return `/admin/clients/${id}`
    // When we're on a historical period, deep-link the client detail page to
    // that month's date range so the orders table filters match.
    const from = `${period.year}-${String(period.month).padStart(2, '0')}-01`
    const lastDay = new Date(period.year, period.month, 0).getDate()
    const to = `${period.year}-${String(period.month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
    return `/admin/clients/${id}?from=${from}&to=${to}`
  }

  const sorted = useMemo(() => {
    return [...clients].sort((a, b) => {
      const av = a[sortCol] ?? (sortCol === 'name' ? '' : 0)
      const bv = b[sortCol] ?? (sortCol === 'name' ? '' : 0)
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [clients, sortCol, sortDir])

  if (isLoading) {
    return (
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {COL_HEADERS.map(h => (
                  <th key={h.key} className={`px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider ${h.align === 'right' ? 'text-right' : 'text-left'}`}>
                    {h.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  {COL_HEADERS.map(h => (
                    <td key={h.key} className="px-3 py-2">
                      <div className="skeleton h-3 rounded" style={{ width: h.key === 'name' ? '120px' : '60px', marginLeft: h.align === 'right' ? 'auto' : undefined }} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  if (clients.length === 0) {
    return (
      <div className="card p-10 text-center">
        <Users size={32} className="mx-auto text-slate-300 mb-2" />
        <p className="text-slate-400 text-sm">No clients yet</p>
      </div>
    )
  }

  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {COL_HEADERS.map(h => (
                <th
                  key={h.key}
                  onClick={() => handleSort(h.key)}
                  className={`px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap select-none cursor-pointer hover:text-slate-700 hover:bg-slate-100 transition-colors ${h.align === 'right' ? 'text-right' : 'text-left'}`}
                >
                  {h.label}
                  <SortIcon col={h.key} sortCol={sortCol} sortDir={sortDir} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sorted.map((c) => {
              const isOverdue = c.overdue
              const isArchived = !!c.archived
              const rowBg = isArchived ? 'bg-amber-50/60 hover:bg-amber-100/50' : (isOverdue ? 'bg-red-50/30 hover:bg-blue-50/40' : 'hover:bg-blue-50/40')
              return (
                <tr
                  key={c.id}
                  onClick={() => navigate(clientHref(c.id))}
                  className={`cursor-pointer transition-colors ${rowBg}`}
                >
                  <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">
                    <span className={`transition-colors ${isArchived ? 'text-amber-800 italic' : 'hover:text-blue-600'}`}>{c.name}</span>
                    {isArchived && (
                      <span className="ml-2 inline-flex items-center gap-0.5 text-amber-700 bg-amber-100 rounded-full px-1.5 py-0.5 text-[10px] font-semibold">
                        <Archive size={9} />
                        Archived
                      </span>
                    )}
                    {isOverdue && (
                      <span className="ml-2 inline-flex items-center gap-0.5 text-red-600 bg-red-100 rounded-full px-1.5 py-0.5 text-[10px] font-semibold">
                        <AlertCircle size={9} />
                        Overdue
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-800">{formatCurrency(c.mtd_total)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">{c.mtd_order_count ?? 0}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">{formatCurrency(c.mtd_orders_fulfilment)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">{formatCurrency(c.mtd_orders_shipping)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">{formatCurrency(c.mtd_sku_charges)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">{formatCurrency(c.mtd_returns)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">{formatCurrency(c.mtd_exports)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">{formatCurrency(c.mtd_storage)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">{formatCurrency(c.mtd_activity)}</td>
                </tr>
              )
            })}
          </tbody>
          {totals && (
            <tfoot className="bg-slate-50 border-t-2 border-slate-200">
              <tr>
                <td className="px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">Total ({clients.length} clients{archivedCount > 0 ? `, incl. ${archivedCount} archived` : ''})</td>
                <td className="px-3 py-2 text-right tabular-nums font-bold text-slate-800">{formatCurrency(totals.mtd_total)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">{totalOrders}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">{formatCurrency(totals.mtd_orders_fulfilment)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">{formatCurrency(totals.mtd_orders_shipping)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">{formatCurrency(totals.mtd_sku_charges)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">{formatCurrency(totals.mtd_returns)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">{formatCurrency(totals.mtd_exports)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">{formatCurrency(totals.mtd_storage)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">{formatCurrency(totals.mtd_activity)}</td>
              </tr>
              {archivedCount > 0 && totalsActive && (
                <tr className="border-t border-slate-200">
                  <td className="px-3 py-2 font-medium text-slate-500 uppercase tracking-wider text-[11px]">Excl. archived ({clients.length - archivedCount} clients)</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-600">{formatCurrency(totalsActive.mtd_total)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{totalOrdersActive}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{formatCurrency(totalsActive.mtd_orders_fulfilment)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{formatCurrency(totalsActive.mtd_orders_shipping)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{formatCurrency(totalsActive.mtd_sku_charges)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{formatCurrency(totalsActive.mtd_returns)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{formatCurrency(totalsActive.mtd_exports)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{formatCurrency(totalsActive.mtd_storage)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{formatCurrency(totalsActive.mtd_activity)}</td>
                </tr>
              )}
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}
